import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setIsChecking(false);
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await fetch(`/api/auth/verify-token?token=${token}`);
                const data = await response.json();
                setIsValid(data.valid);
            } catch (err) {
                setIsValid(false);
            } finally {
                setIsChecking(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!password || password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères.');
            return;
        }
        if (password !== confirm) {
            setError('Les mots de passe ne correspondent pas.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password })
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Erreur lors de la réinitialisation.');
            }

            setSuccess(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (isChecking) {
        return (
            <div className="bg-background-light dark:bg-background-dark min-h-screen flex items-center justify-center font-display">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!token || !isValid) {
        return (
            <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col font-display transition-colors duration-300">
                <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-gradient-mesh px-4">
                    <div className="w-full max-w-[440px] flex flex-col items-center">
                        <div className="w-full bg-white dark:bg-slate-900/80 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                                <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-3xl">error</span>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Lien invalide ou expiré</h2>
                            <p className="text-slate-600 dark:text-slate-400 mb-8">
                                Ce lien de réinitialisation n'est plus valide. Il a peut-être déjà été utilisé ou a expiré (validité de 1 heure).
                            </p>
                            <Link
                                to="/forgot-password"
                                className="w-full inline-flex justify-center items-center h-14 bg-primary hover:bg-orange-600 text-white font-bold rounded-xl transition-all shadow-lg pb-1"
                            >
                                Demander un nouveau lien
                            </Link>
                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-slate-500 hover:text-primary transition-colors text-sm font-medium">
                                    ← Retour à la connexion
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col font-display transition-colors duration-300">
            <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-gradient-mesh px-4">
                <div className="w-full max-w-[440px] flex flex-col items-center">
                    <div className="mb-10 flex flex-col items-center gap-2">
                        <div className="size-24 bg-white dark:bg-slate-900 rounded-3xl flex items-center justify-center border border-slate-200 dark:border-slate-800 shadow-xl shadow-primary/5 p-4 mb-4">
                            <img src="/Stockizio.png" alt="Stockizio Logo" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-slate-900 dark:text-slate-100 text-4xl font-black tracking-tighter mt-6">
                            Stock<span className="text-primary">izio</span>
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium uppercase tracking-widest mt-1 opacity-80">Nouveau mot de passe</p>
                    </div>

                    <div className="w-full bg-white dark:bg-slate-900/80 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800">
                        {success ? (
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                                    <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-3xl">check</span>
                                </div>
                                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Mot de passe modifié !</h2>
                                <p className="text-slate-600 dark:text-slate-400 mb-6 font-medium">
                                    Redirection vers la page de connexion...
                                </p>
                            </div>
                        ) : (
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {error && <div className="mb-4 text-[#ec5b13] text-sm font-semibold text-center bg-[#ec5b13]/10 py-3 rounded-lg border border-[#ec5b13]/20">{error}</div>}

                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">Nouveau mot de passe</label>
                                        <div className="relative group">
                                            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">key</span>
                                            <input
                                                className="w-full pl-12 pr-4 h-14 bg-slate-50 dark:bg-[#261b16] border border-slate-200 dark:border-primary/20 rounded-xl text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none"
                                                placeholder="••••••••"
                                                type="password"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                required
                                                minLength={6}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">Confirmer le mot de passe</label>
                                        <div className="relative group">
                                            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">key</span>
                                            <input
                                                className="w-full pl-12 pr-4 h-14 bg-slate-50 dark:bg-[#261b16] border border-slate-200 dark:border-primary/20 rounded-xl text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none"
                                                placeholder="••••••••"
                                                type="password"
                                                value={confirm}
                                                onChange={e => setConfirm(e.target.value)}
                                                required
                                                minLength={6}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <button
                                    className="w-full h-14 bg-primary hover:bg-orange-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary/30 active:scale-[0.98] outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    disabled={loading}
                                    type="submit"
                                >
                                    {loading ? "Validation..." : "Valider"}
                                </button>
                            </form>
                        )}

                        {!success && (
                            <div className="mt-8 pt-6 border-t border-slate-100 dark:border-primary/10 text-center">
                                <Link to="/login" className="text-primary font-semibold hover:underline flex items-center justify-center gap-2">
                                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                                    Retour à la connexion
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
